import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Check } from "lucide-react";
import { siteContent } from "@/content/site";

const hero = siteContent.hero;

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-28 lg:pt-36 pb-16 lg:pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      {/* organic blobs - brand teal/green/amber */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#5BAE71]/22 blur-3xl animate-float-slow" />
        <div className="absolute top-1/3 -right-32 w-[480px] h-[480px] rounded-full bg-[#E8A33D]/20 blur-3xl animate-float-slow" />
        <div className="absolute bottom-0 left-1/3 w-[300px] h-[300px] rounded-full bg-[#1F4E5F]/14 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-10 lg:mb-14"
        >
          <span
            data-testid="hero-overline"
            className="inline-flex items-center gap-2 bg-[#E1EEEA] text-[#1F4E5F] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.18em]"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#E8A33D]" /> {hero.eyebrow}
          </span>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0E2A36] leading-[1.05]">
            {hero.h1.line1}{" "}
            <span className="text-[#1F4E5F] italic font-medium">{hero.h1.line2}</span>
          </h1>
          <p className="mt-5 text-lg text-[#3D5560] leading-relaxed max-w-2xl mx-auto">
            {hero.subhead}
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              data-testid="hero-cta-beta"
              onClick={() => scrollTo("beta")}
              className="group inline-flex items-center justify-center gap-2 bg-[#1F4E5F] text-[#F4FAF8] hover:bg-[#173E50] px-7 py-3.5 rounded-full font-semibold shadow-sm transition-all hover:gap-3"
            >
              {hero.primaryCta}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              data-testid="hero-cta-how"
              onClick={() => scrollTo("how")}
              className="inline-flex items-center justify-center gap-2 border-2 border-[#1F4E5F]/15 hover:border-[#1F4E5F] text-[#1F4E5F] px-7 py-3.5 rounded-full font-semibold transition-all bg-white/60 hover:bg-white"
            >
              {hero.secondaryCta}
            </button>
          </div>
          <p className="mt-3 text-xs text-[#6A8290]">{hero.disclaimer}</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-5 lg:gap-7">
          <HeroCard
            tag={hero.adultChildCard.tag}
            title={hero.adultChildCard.title}
            blurb={hero.adultChildCard.blurb}
            bullets={hero.adultChildCard.bullets}
            image="https://images.unsplash.com/photo-1557862921-37829c790f19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBhZHVsdCUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3ODA5MTk2ODl8MA&ixlib=rb-4.1.0&q=85"
            imageAlt={hero.adultChildCard.imageAlt}
            variant="dark"
            cta={hero.adultChildCard.cta}
            testId="hero-card-adult-child"
          />
          <HeroCard
            tag={hero.seniorCard.tag}
            title={hero.seniorCard.title}
            blurb={hero.seniorCard.blurb}
            bullets={hero.seniorCard.bullets}
            image="https://customer-assets.emergentagent.com/job_design-upgrade-217/artifacts/7tfgfy7b_Gemini_Generated_Image_qhotjtqhotjtqhot.png"
            imageAlt={hero.seniorCard.imageAlt}
            imagePosition="38% 22%"
            variant="light"
            cta={hero.seniorCard.cta}
            testId="hero-card-senior"
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-14 grid grid-cols-3 gap-4 max-w-3xl mx-auto"
          data-testid="hero-stats"
        >
          {hero.stats.map((s, i) => (
            <div
              key={i}
              className="text-center px-4 py-5 rounded-2xl bg-white/70 backdrop-blur-sm border border-[#C9DDD6]"
            >
              <div className="font-display text-2xl sm:text-3xl font-bold text-[#1F4E5F]">
                {s.v}
              </div>
              <div className="text-xs sm:text-sm text-[#6A8290] mt-1">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function HeroCard({ tag, title, blurb, bullets, image, imageAlt, variant, cta, testId, imagePosition }) {
  const isDark = variant === "dark";
  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
      className={`relative rounded-3xl overflow-hidden border ${
        isDark
          ? "bg-[#1F4E5F] text-[#F4FAF8] border-[#1F4E5F]"
          : "bg-[#E1EEEA] text-[#0E2A36] border-[#C9DDD6]"
      } p-6 lg:p-9`}
    >
      <div className="grid grid-cols-3 gap-5">
        <div className="col-span-2 flex flex-col">
          <span
            className={`text-xs font-bold uppercase tracking-[0.18em] mb-3 ${
              isDark ? "text-[#E8A33D]" : "text-[#E8A33D]"
            }`}
          >
            {tag}
          </span>
          <h3 className="font-display text-2xl lg:text-[28px] font-semibold leading-tight">
            {title}
          </h3>
          <p
            className={`mt-3 text-sm lg:text-[15px] leading-relaxed ${
              isDark ? "text-[#F4FAF8]/75" : "text-[#3D5560]"
            }`}
          >
            {blurb}
          </p>
          <ul className="mt-5 space-y-2.5">
            {bullets.map((b, i) => (
              <li key={i} className="flex gap-2 text-sm">
                <Check
                  className={`w-4 h-4 mt-0.5 shrink-0 ${
                    isDark ? "text-[#E8A33D]" : "text-[#1F4E5F]"
                  }`}
                />
                <span className={isDark ? "text-[#F4FAF8]/85" : "text-[#3D5560]"}>
                  {b}
                </span>
              </li>
            ))}
          </ul>
          <button
            data-testid={`${testId}-cta`}
            onClick={() => scrollTo("beta")}
            className={`mt-6 self-start inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:gap-3 ${
              isDark
                ? "bg-[#E8A33D] text-white hover:bg-[#C88A28]"
                : "bg-[#1F4E5F] text-[#F4FAF8] hover:bg-[#173E50]"
            }`}
          >
            {cta} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="col-span-1 relative">
          <div className="absolute inset-0 -mr-6 lg:-mr-9 rounded-l-3xl overflow-hidden">
            <img
              src={image}
              alt={imageAlt}
              className="w-full h-full object-cover"
              style={{ objectPosition: imagePosition || "center" }}
              loading="lazy"
            />
            <div
              className={`absolute inset-0 ${
                isDark
                  ? "bg-gradient-to-l from-transparent to-[#1F4E5F]/40"
                  : "bg-gradient-to-l from-transparent to-[#E1EEEA]/40"
              }`}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
